import React from "react";
import { cn } from "@/lib/utils";
import {
  notificationSeverityTokens,
  type NotificationSeverityName,
} from "@/lib/tokens";

type InlineAlertLayout = "default" | "compact" | "field";

interface AlertContentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone: NotificationSeverityName;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  layout?: InlineAlertLayout;
  id?: string;
}

const AlertContent = React.forwardRef<HTMLDivElement, AlertContentProps>(
  (
    {
      tone,
      title,
      description,
      icon,
      actions,
      children,
      className,
      layout = "default",
      id,
      style,
      role,
      ...rest
    },
    ref,
  ) => {
    const tokens = notificationSeverityTokens[tone];
    const paddingClass = (() => {
      switch (layout) {
        case "field":
          return "px-3 py-2";
        case "compact":
          return "px-3 py-2";
        default:
          return "px-3 py-3";
      }
    })();
    const textClass = layout === "field" ? "text-sm" : "text-sm";

    const mergedStyle: React.CSSProperties = {
      backgroundColor: tokens.surface,
      borderColor: tokens.border,
      color: tokens.text,
      ...style,
    };

    return (
      <div
        ref={ref}
        id={id}
        role={role ?? "alert"}
        className={cn(
          "w-full rounded-md border flex items-start gap-3",
          paddingClass,
          className,
        )}
        style={mergedStyle}
        {...rest}
      >
        {icon ? (
          <div className="mt-0.5 flex h-5 w-5 items-center text-current">{icon}</div>
        ) : null}
        <div className={cn("flex-1 min-w-0 space-y-1", textClass)}>
          {title ? (
            <div className="font-semibold text-slate-900 leading-tight">
              {title}
            </div>
          ) : null}
          {description ? (
            <div className="text-slate-700 leading-relaxed">{description}</div>
          ) : null}
          {children}
          {actions ? <div className="mt-2 flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      </div>
    );
  },
);
AlertContent.displayName = "AlertContent";

export interface InlineAlertProps extends Omit<AlertContentProps, "layout"> {
  layout?: InlineAlertLayout;
}

export const InlineAlert = React.forwardRef<HTMLDivElement, InlineAlertProps>(
  ({ layout = "default", ...rest }, ref) => (
    <AlertContent ref={ref} layout={layout} {...rest} />
  ),
);
InlineAlert.displayName = "InlineAlert";

export interface TableInlineAlertProps
  extends Omit<AlertContentProps, "layout"> {
  colSpan?: number;
  className?: string;
}

export const TableInlineAlert = React.forwardRef<HTMLTableRowElement, TableInlineAlertProps>(
  ({ colSpan = 1, className, ...content }, ref) => (
    <tr ref={ref} className={className}>
      <td colSpan={colSpan} className="p-0">
        <AlertContent layout="compact" {...content} />
      </td>
    </tr>
  ),
);
TableInlineAlert.displayName = "TableInlineAlert";

export interface FieldInlineAlertProps
  extends Omit<AlertContentProps, "layout" | "title" | "actions">,
    React.HTMLAttributes<HTMLDivElement> {
  message?: React.ReactNode;
}

export const FieldInlineAlert = React.forwardRef<HTMLDivElement, FieldInlineAlertProps>(
  ({ tone = "error", description, message, className, id, ...rest }, ref) => (
    <AlertContent
      ref={ref}
      layout="field"
      tone={tone}
      description={description ?? message}
      className={className}
      id={id}
      {...rest}
    />
  ),
);
FieldInlineAlert.displayName = "FieldInlineAlert";
